import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProperty } from '@features/properties';
import {
  NafahLogin,
  WelcomeScreen,
  EligibilityCheck,
  UserProfileForm,
  BankOffers,
  PromissoryNote,
  RentalAgreement,
  KeyPickup,
} from '@features/purchase-journey';

export default function Purchase() {
  const { propertyId } = useParams();

  // Flow: login → welcome → eligibility → profile → offers → promissory → rental → keyPickup
  const [step, setStep] = useState('login');
  const [userData, setUserData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);

  const { data: property } = useProperty(propertyId);

  // Step 1: Nafath login complete → Welcome screen
  const handleNafahSuccess = (data) => {
    setUserData(data);
    setStep('welcome');
    window.scrollTo(0, 0);
  };

  // Step 2: Welcome continue → Eligibility check
  const handleWelcomeContinue = () => {
    setStep('eligibility');
    window.scrollTo(0, 0);
  };

  // Step 3: Eligibility complete → Profile form (receives profile data from external services)
  const handleEligibilityComplete = (profile) => {
    setUserProfile(profile);
    setStep('profile');
    window.scrollTo(0, 0);
  };

  // Step 4: Profile form submit → Bank offers
  const handleProfileSubmit = (updatedProfile) => {
    setUserProfile(updatedProfile);
    // Update userData with profile for use in later steps
    setUserData((prev) => ({ ...prev, profile: updatedProfile }));
    setStep('offers');
    window.scrollTo(0, 0);
  };

  // Step 5: Bank selection → Promissory note
  const handleBankSelect = (bank) => {
    setSelectedBank(bank);
    setStep('promissory');
    window.scrollTo(0, 0);
  };

  // Step 6: Promissory complete → Rental agreement
  const handlePromissoryComplete = () => {
    setStep('rental');
    window.scrollTo(0, 0);
  };

  // Step 7: Rental complete → Key pickup
  const handleRentalComplete = () => {
    setStep('keyPickup');
    window.scrollTo(0, 0);
  };

  // Step 8: Key pickup complete → Home
  const handleKeyPickupComplete = () => {
    window.location.href = '/';
  };

  // Render current step
  switch (step) {
    case 'login':
      return <NafahLogin onSuccess={handleNafahSuccess} />;

    case 'welcome':
      return (
        <WelcomeScreen
          firstName={userData?.firstName}
          onContinue={handleWelcomeContinue}
        />
      );

    case 'eligibility':
      return <EligibilityCheck onComplete={handleEligibilityComplete} />;

    case 'profile':
      return (
        <UserProfileForm
          profile={userProfile}
          onSubmit={handleProfileSubmit}
        />
      );

    case 'offers':
      return <BankOffers propertyPrice={property?.price} onSelect={handleBankSelect} />;

    case 'promissory':
      return (
        <PromissoryNote
          bankOffer={selectedBank}
          userData={userData}
          onComplete={handlePromissoryComplete}
        />
      );

    case 'rental':
      return (
        <RentalAgreement
          property={property}
          userData={userData}
          onComplete={handleRentalComplete}
        />
      );

    case 'keyPickup':
      return <KeyPickup property={property} onComplete={handleKeyPickupComplete} />;

    default:
      return <NafahLogin onSuccess={handleNafahSuccess} />;
  }
}
